import HotelGuest from './components/hotel/HotelGuest';
import React, {useEffect} from 'react';
import { Routes, Route, Navigate } from "react-router-dom";
import { inject, observer } from "mobx-react";
import { DEFAULT_EMAIL, DEFAULT_PSW } from "./assets/globalConstants";
import Home from './components/home/Home';
import HomeBody from './components/HomeBody';
import Settings from './components/account/settings';
import TermsNConditions from './components/terms/termsnconditions';
import About from './components/about';
import Favorites from './components/favorites';
import Contact from './components/contact/Contact';
import PrivacyPolicy from './components/terms/privacypolicy';
import DeliveryOptions from './components/terms/deliveryOptions';
import PaymentOptions from './components/terms/paymentOptions';
import Cart from './components/cart/Cart';
import Reservation from './components/reservation/Reservation';
import Success from './components/checkout/success';
import Fail from './components/checkout/Fail';
import VivaPage from './components/checkout/VivaPage';

import Product from './components/product';
import Checkout from './components/checkout/Checkout'
import Account from './components/account/Account';
import CategoryView from './components/categoryView';
import OrderTracking from './components/tracking/OrderTracking';
import Faq from './components/terms/faq';
import ScrollToTop from './components/ScrollToTop';
import { DOCUMENT_TITLE, META_DESCRIPTION, META_ROBOTS } from "./assets/globalConstants";
import { SUPPORTED_LANGUAGES } from './hooks/_useTranslate';
import { bootstrapColorMode } from './theme/colorMode';
import { bootstrapTheme, syncThemeFromSettings } from './theme/brandTheme';

// Apply the BRAND theme synchronously at module load, BEFORE React renders, so
// the per-theme CSS variables are present on first paint (no flash, and
// useThemeColor reads real values on first render). The theme is dashboard-set
// (backend settings.theme), cached in localStorage, falling back to constants.js
// THEME on the first visit — see theme/brandTheme.js.
if (typeof document !== 'undefined') {
  bootstrapTheme();
  // Then layer the light/dark choice (manual override over the OS) on top —
  // adds .dark-mode to <html> when dark is effective, before first paint.
  bootstrapColorMode();
}


const Main = ({feathersStore})=> {

  // Booking availability, resolved from the tenant's settings (see the /reservation route below).
  // `settings` is initialised to {} and populated one socket round-trip after mount, so "no keys
  // yet" is a third state - unknown - and must not be read as "off".
  const _settings = feathersStore?.settings || {};
  const bookingKnown = Object.keys(_settings).length > 0;
  //showBooking absent = on, so a tenant that has never been given the key keeps today's behaviour
  const bookingOn = bookingKnown && _settings.showBooking !== false && !!_settings.appBooking;

  useEffect(() => {
    if(DOCUMENT_TITLE)document.title = DOCUMENT_TITLE; 
    if(META_DESCRIPTION)setMeta("description", META_DESCRIPTION);
    if(META_ROBOTS)setMeta("robots", META_ROBOTS);
    connectToServer();
    createPaymentMethodLS();
    getLanguage();
  }, []);

  // Reconcile the dashboard-set brand theme (backend settings.theme) with what's
  // cached/painted; caches it and reloads once if an admin changed it (brandTheme.js).
  useEffect(() => {
    if(feathersStore.settings?.theme) syncThemeFromSettings(feathersStore.settings.theme);
  }, [feathersStore.settings?.theme]);

  useEffect(() => {
    if(feathersStore.settings?.currency){
      switch(feathersStore.settings.currency){
        case "USD" : 
          feathersStore.setCurrency("$")
          break;
        case "GBP" : 
          feathersStore.setCurrency("£")
          break;
        case "EUR" : 
          feathersStore.setCurrency("€")
          break;
        default: feathersStore.setCurrency("€")
      }
    }
  }, [feathersStore.settings?.currency]); 

  function setMeta(metaName, val) {
    const metas = document.getElementsByTagName('meta');
  
    for (let i = 0; i < metas.length; i++) {
      if (metas[i].getAttribute('name') === metaName) {
        return metas[i].setAttribute('content', val);
      }
    }
  
    return '';
  }

  const connectToServer = async() => {
    await feathersStore.connect();    
    if(!feathersStore.isAuthenticated)feathersStore.login(DEFAULT_EMAIL, DEFAULT_PSW)
    .then(async(auth) => {         
          await feathersStore.setObservables();                          
    })
    .catch(error => window.alert(`Login error: ${error.message}`));   
  }

  const createPaymentMethodLS = () => {
    //vestigial default seed (never read/matched/displayed elsewhere); localize so non-Greek shops don't seed a Greek literal
    const greekShop = !feathersStore.settings?.masterLanguage || feathersStore.settings?.masterLanguage === "el";
    !localStorage.getItem('Payment_Method') &&
      localStorage.setItem('Payment_Method',JSON.stringify({
          name: greekShop ? 'Μετρητά κατά την παράδοση' : 'Cash on delivery',
          index: 0
      }))
  }

  const getLanguage = () => {
    if(!localStorage.getItem('Language')){
      const userLanguage = window.navigator?.userLanguage ?? window.navigator.language;
      // navigator.language used to be persisted unvalidated, so a browser set to a language with
      // no locale bundle (cy, pl, ur...) was written straight into storage and every label
      // rendered blank. Seed only a SUPPORTED code; otherwise fall back to the tenant's
      // masterLanguage, and to English when that is unset/unsupported too.
      const seeded = (userLanguage ?? "").slice(0, 2).toLowerCase();
      const master = feathersStore.settings?.masterLanguage;
      const resolved = SUPPORTED_LANGUAGES.includes(seeded) ? seeded
        : (SUPPORTED_LANGUAGES.includes(master) ? master : "en");
      localStorage.setItem('Language', resolved);
    }
    feathersStore.setLanguage(localStorage.getItem('Language'))
  }

  return (
    <div className="body-container">
      <ScrollToTop>
      <Routes>
        <Route path="/hotel" element={<HotelGuest/>}/>
        <Route path="/"  element={<Home/>}>  
          <Route path="cart" element={<Cart/>}/>
          <Route path="about" element={<About/>}/>         
          <Route path="product/:name" element={<Product/>}/>      
          <Route path="checkout"element={<Checkout/>}/>
          <Route path="favorites"element={<Favorites/>}/>
          <Route path="contact"element={<Contact/>}/>
          <Route path="settings" component={<Settings/>} />        
          <Route path="termsnconditions"element={<TermsNConditions/>}/>
          <Route path="privacypolicy"element={<PrivacyPolicy/>}/> 
          <Route path="paymentoptions"element={<PaymentOptions/>}/>
          <Route path="deliveryoptions"element={<DeliveryOptions/>}/> 
          <Route path="faq" element={<Faq/>}/>     
          {/* Hiding the button was never a gate: this route was registered unconditionally, so a
              typed, bookmarked or shared /reservation link reached the full form and could file a
              REAL booking on a shop that has booking switched off. Three-state on purpose -
              feathersStore.settings starts as {} (truthy but keyless) and fills one socket
              round-trip after mount, so a plain falsy test would bounce every visitor during that
              window, including where booking is on. While settings are unknown the route simply is
              not registered; once known it either renders or redirects home.
              showBooking is the master switch (absent = on, so no tenant changes on release) and
              appBooking stays the owner's customer-facing switch. */}
          {bookingKnown && (bookingOn
            ? <Route path="reservation" element={<Reservation/>}/>
            : <Route path="reservation" element={<Navigate to="/" replace/>}/>)}
          <Route path="account"  element={<Account/>} />
          <Route path="viva/success"  element={<Success/>} />
          <Route path="viva/fail"  element={<Fail/>} />
          <Route path="VivaPage"  element={<VivaPage/>} />
          {/* NOTE: `element=`, not `component=` — the settings route above uses
              the React Router v5 `component` prop under v6 and renders nothing. */}
          <Route path="order/:id" element={<OrderTracking/>} />
          <Route path="Home/:name" element={<CategoryView/>} />
          <Route path="Home" element={<HomeBody/>} />   
          <Route index element={<HomeBody/>} />         
        </Route>
      </Routes>
      </ScrollToTop>
    </div>
    );
  
}

export default inject('feathersStore')(observer(Main));